import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  PageContentMenu,
  useRestaurantStore,
  useCartStore,
  useAuthStore,
  useUIStore,
  useOrderStore,
  Sheet,
  SheetContentProduct,
  OptionRow,
  buildSyntheticSizeGroup,
  getLowestVariantPrice,
  SYNTHETIC_SIZE_GROUP_ID,
  isSyntheticSizeOption,
  variantKeyFromOptionId,
} from '@restaurant/core'
import type { MenuItem, TileProductProps } from '@restaurant/core'
import { WebFavoritesPageService } from '@restaurant/viewmodels'
import { safeGetLogger } from '@restaurant/logging'
import { isShowcase } from '../showcase/showcase'
import { showShowcaseNotice } from '../showcase/ShowcaseNotice'


// How long a deep-linked item id may stay unresolved before we TELL THE DINER
// we can't find it. This gates the notice only — never the ability to resolve
// the id (see the resolution effect below). That distinction matters because no
// wall-clock budget can tell "genuinely absent" from "still arriving": a full
// menu payload on congested LTE regularly takes several seconds, so any timer
// short enough to feel responsive will sometimes fire on a live item. So the
// worst case here is "notice fired a bit early, then the sheet opened anyway",
// which is survivable — whereas "notice fired and the item is now unreachable"
// is the bug this whole file is structured to prevent.
const DEEP_LINK_MISS_GRACE_MS = 2500

export default function MenuPage() {
  const navigate = useNavigate()
  // Per-item deep link — /menu/item/:itemId. `itemId` is the client
  // `MenuItem.id` (DB menuitems.menu_item_id uuid). Undefined on /menu itself.
  const { itemId } = useParams<{ itemId?: string }>()
  const categories = useRestaurantStore((state) => state.categories)
  const isLoading = useRestaurantStore((state) => state.isLoadingMenu)
  const currentRestaurant = useRestaurantStore((state) => state.currentRestaurant)
  const getMenuItemById = useRestaurantStore((state) => state.getMenuItemById)
  const addItem = useCartStore((state) => state.addItem)
  const user = useAuthStore((state) => state.user)
  const orderHistory = useOrderStore((state) => state.orderHistory)

  const requestAuth = useUIStore((s) => s.requestAuth)
  const isAuthSheetOpen = useUIStore((s) => s.isAuthSheetOpen)
  const invalidateFavoritesCount = useUIStore((s) => s.invalidateFavoritesCount)
  const favoritesCount = useUIStore((s) => s.favoritesCount)
  const showToast = useUIStore((s) => s.showToast)

  // Memoize the favorites service instance
  const favoritesService = useMemo(() => new WebFavoritesPageService(), [])

  // Product sheet state
  const [isProductSheetOpen, setIsProductSheetOpen] = useState(false)
  const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItem | null>(null)
  const syntheticSizeGroup = useMemo(
    () => buildSyntheticSizeGroup(selectedMenuItem),
    [selectedMenuItem]
  )
  const [quantity, setQuantity] = useState(1)
  const [selectedCustomizations, setSelectedCustomizations] = useState<Record<string, Set<string>>>({})
  const [isFavorited, setIsFavorited] = useState(false)
  const [isFavoriteLoading, setIsFavoriteLoading] = useState(false)

  // Pending favorite auth — saves product sheet state while the user authenticates
  const pendingFavoriteAuth = useRef<{
    menuItem: MenuItem
    quantity: number
    customizations: Record<string, Set<string>>
  } | null>(null)
  const shouldAutoFavorite = useRef(false)

  // When user authenticates while we have a pending favorite, restore the
  // product sheet with saved state and auto-favorite the item.
  useEffect(() => {
    if (!pendingFavoriteAuth.current || !user?.id) return

    const saved = pendingFavoriteAuth.current
    pendingFavoriteAuth.current = null
    shouldAutoFavorite.current = true
    setSelectedMenuItem(saved.menuItem)
    setQuantity(saved.quantity)
    setSelectedCustomizations(saved.customizations)
    setIsProductSheetOpen(true)
  }, [user])

  // When auth sheet closes without auth, restore the product sheet with
  // saved state and default (unfavorited) heart state.
  const prevAuthSheetOpen = useRef(false)
  useEffect(() => {
    const wasOpen = prevAuthSheetOpen.current
    prevAuthSheetOpen.current = isAuthSheetOpen

    // Detect close transition while we have pending state and no user
    if (wasOpen && !isAuthSheetOpen && pendingFavoriteAuth.current && !user?.id) {
      const saved = pendingFavoriteAuth.current
      pendingFavoriteAuth.current = null
      setSelectedMenuItem(saved.menuItem)
      setQuantity(saved.quantity)
      setSelectedCustomizations(saved.customizations)
      setIsFavorited(false)
      setIsProductSheetOpen(true)
    }
  }, [isAuthSheetOpen, user])

  // Check favorite status when a product is selected, and auto-favorite
  // if the user just authenticated via the favorite-triggered auth flow.
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!selectedMenuItem || !user?.id || !currentRestaurant?.id) {
        setIsFavorited(false)
        return
      }

      try {
        const favorited = await favoritesService.isFavorited(
          selectedMenuItem.id,
          user.id,
          currentRestaurant.id
        )
        setIsFavorited(favorited)

        // Auto-favorite if the user just authenticated to favorite this item
        if (shouldAutoFavorite.current && !favorited) {
          shouldAutoFavorite.current = false
          // Build selected options for the favorite
          const selectedOptionsArray = Object.entries(selectedCustomizations)
            .filter(([customizationId]) => !isSyntheticSizeOption(customizationId))
            .flatMap(([customizationId, optionIds]) =>
              Array.from(optionIds).map(optionId => ({
                customizationId,
                optionId,
                name: selectedMenuItem.customizations
                  ?.find(c => c.id === customizationId)
                  ?.options.find(o => o.id === optionId)?.name || ''
              }))
            )

          const result = await favoritesService.toggleFavorite(
            selectedMenuItem,
            user.id,
            currentRestaurant.id,
            selectedOptionsArray.length > 0 ? selectedOptionsArray : undefined
          )
          if (result.success) {
            setIsFavorited(true)
            invalidateFavoritesCount()
          }
        } else {
          shouldAutoFavorite.current = false
        }
      } catch (error) {
        safeGetLogger()?.error('Favorite status check failed', { domain: 'favorites', error: String(error) })
        setIsFavorited(false)
        shouldAutoFavorite.current = false
      }
    }

    if (isProductSheetOpen && selectedMenuItem) {
      checkFavoriteStatus()
    }
  }, [isProductSheetOpen, selectedMenuItem, user?.id, currentRestaurant?.id, favoritesService, selectedCustomizations, invalidateFavoritesCount])

  // Handle favorite toggle
  const handleFavoriteToggle = useCallback(async () => {
    if (!selectedMenuItem || isFavoriteLoading) return

    if (!user?.id) {
      // Save product sheet state, close it, and request auth
      pendingFavoriteAuth.current = {
        menuItem: selectedMenuItem,
        quantity,
        customizations: selectedCustomizations,
      }
      setIsProductSheetOpen(false)
      requestAuth()
      return
    }

    if (!currentRestaurant?.id) return

    setIsFavoriteLoading(true)

    try {
      // Build selected options for the favorite (preserving customizations)
      const selectedOptionsArray = Object.entries(selectedCustomizations)
        .filter(([customizationId]) => !isSyntheticSizeOption(customizationId))
        .flatMap(([customizationId, optionIds]) =>
          Array.from(optionIds).map(optionId => ({
            customizationId,
            optionId,
            // Find option name for display
            name: selectedMenuItem.customizations
              ?.find(c => c.id === customizationId)
              ?.options.find(o => o.id === optionId)?.name || ''
          }))
      )

      const result = await favoritesService.toggleFavorite(
        selectedMenuItem,
        user.id,
        currentRestaurant.id,
        selectedOptionsArray.length > 0 ? selectedOptionsArray : undefined
      )

      if (result.success) {
        setIsFavorited(!isFavorited)
        invalidateFavoritesCount()
      }
    } catch (error) {
      safeGetLogger()?.error('Favorite toggle failed', { domain: 'favorites', error: String(error) })
    } finally {
      setIsFavoriteLoading(false)
    }
  }, [selectedMenuItem, user?.id, currentRestaurant?.id, isFavoriteLoading, isFavorited, selectedCustomizations, favoritesService, invalidateFavoritesCount, requestAuth])

  // Transform store data to PageContentMenu props format
  const formattedCategories = categories.map(category => ({
    id: category.id,
    categoryName: category.name,
    products: category.items.map(item => ({
      productName: item.name,
      productDescription: item.description || '',
      priceCurrent: (() => {
        const hasMultipleVariants = Object.keys(item.priceVariants ?? {}).length >= 2
        const hasPaidCustomizations = item.customizations?.some(c =>
          c.options.some(o => o.price > 0)
        )
        // No price data at all → no price line (never fabricate $0.00).
        if (!hasMultipleVariants && (item.price === undefined || item.price === null)) {
          return ''
        }
        const basePrice = hasMultipleVariants
          ? getLowestVariantPrice(item)
          : item.price ?? 0
        const prefix = hasMultipleVariants || hasPaidCustomizations ? 'From ' : ''
        return `${prefix}$${basePrice.toFixed(2)}`
      })(),
      imageUrl: item.imageUrl,
      showImageProductTile: !!item.imageUrl,
      showProductDescription: true,
    })),
    // Default props for MenuCategory component
    borderSide: 'left' as const,
  }))

  // Opens the product sheet for an already-resolved MenuItem: resets
  // quantity, defaults customizations (including the synthetic Size group),
  // and opens the sheet. Shared by the grid click path and the per-item
  // deep-link path so both produce identical sheet/customization state.
  const openProductSheetForItem = useCallback((menuItem: MenuItem) => {
    setSelectedMenuItem(menuItem)

    // Reset state for new product
    setQuantity(1)

    // Initialize customizations with defaults
    const initialCustomizations: Record<string, Set<string>> = {}
    menuItem.customizations?.forEach(customization => {
      const defaultOptions = customization.options
        .filter(opt => opt.isDefault)
        .map(opt => opt.id)
      if (defaultOptions.length > 0) {
        initialCustomizations[customization.id] = new Set(defaultOptions)
      }
    })
    // Auto-select lowest-price variant in synthetic Size group
    const sizeGroup = buildSyntheticSizeGroup(menuItem)
    if (sizeGroup) {
      const defaultOption = sizeGroup.options.find(opt => opt.isDefault)
      if (defaultOption) {
        initialCustomizations[SYNTHETIC_SIZE_GROUP_ID] = new Set([defaultOption.id])
      }
    }

    setSelectedCustomizations(initialCustomizations)

    setIsProductSheetOpen(true)
  }, [])

  const handleProductClick = (product: TileProductProps, categoryId: string, productIndex: number) => {
    // `TileProductProps` carries no `id`/`name`; read them loosely to preserve
    // the existing telemetry payload (the real values come from the resolved
    // menuItem below — this top-level log predates that lookup).
    const loggedProduct = product as { id?: unknown; name?: unknown }
    safeGetLogger()?.menu('item_viewed', { productId: loggedProduct.id, productName: loggedProduct.name })
    // Find the original MenuItem from the store
    const category = categories.find(cat => cat.id === categoryId)
    if (category && category.items[productIndex]) {
      // The diner has taken over. Permanently disables deep-link auto-open for
      // this mount — see `dinerOpenedSheetRef`.
      dinerOpenedSheetRef.current = true
      openedFromDeepLinkRef.current = false
      openProductSheetForItem(category.items[productIndex])
    }
  }

  // Tracks whether the currently-open sheet was opened via the /menu/item/:itemId
  // deep link, so closing it can clean the id back out of the URL.
  const openedFromDeepLinkRef = useRef(false)
  // Ids we have ALREADY auto-opened the sheet for during this mount.
  //
  // What this guarantees: auto-open happens AT MOST ONCE per item id per mount.
  // The id goes in on the first successful open and never comes out — not when
  // the diner closes the sheet, not when a later menu write drops the item. It
  // is deliberately not a give-up latch either, because it is only ever written
  // on a HIT: an id that has never resolved is never in here, so resolution
  // retry stays unbounded (see the resolution effect).
  //
  // Why "never comes out": we have already done the one thing the link promised
  // — we showed the diner the item. Re-opening on every later write turns a
  // flapping menu slot into an unbounded close/reopen loop in the diner's face.
  const autoOpenedItemIdsRef = useRef<Set<string>>(new Set())
  // Set the moment the diner opens a sheet themselves from the grid. Once set it
  // stays set for the whole mount: a diner who has taken the wheel must never
  // have a sheet swapped out from under them by a late-resolving deep link.
  const dinerOpenedSheetRef = useRef(false)
  // Ids we've already shown the unavailable notice for, so a retry can never
  // toast the same id twice. Refs (not state) so React 18 StrictMode's
  // double-invoked effects and re-mounts don't duplicate the notice.
  const noticedDeepLinkItemIdsRef = useRef<Set<string>>(new Set())
  // A miss is not final until this grace window elapses without the menu being
  // rewritten with the item present.
  const missGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const missGraceItemIdRef = useRef<string | null>(null)

  const clearMissGrace = useCallback(() => {
    if (missGraceTimerRef.current !== null) {
      clearTimeout(missGraceTimerRef.current)
      missGraceTimerRef.current = null
    }
    missGraceItemIdRef.current = null
  }, [])

  // Show the unavailable notice at most once per item id. Shared by BOTH the
  // miss-grace path and the vanish-close path so a diner is told once, period.
  //
  // Copy note: NOT "no longer available". The menu is keyed to
  // `currentLocation`; a deep-linked item can still miss when the linked item
  // belongs to a different location than the diner's current one, so the
  // usual truth is "wrong location", and telling someone an item they can order
  // where they are standing is dead is simply false.
  const noticeUnavailableOnce = useCallback((id: string) => {
    if (noticedDeepLinkItemIdsRef.current.has(id)) return
    noticedDeepLinkItemIdsRef.current.add(id)
    showToast("This item isn't available at this location.")
  }, [showToast])

  // Deep-link resolution.
  //
  // Two things make this trickier than a single lookup:
  //  1. `categories` still rewrites (location switch triggers a refetch), so a
  //     miss can become a hit — retry stays unbounded on every write.
  //  2. `isLoadingMenu` is now live (set by `useMenuData`), but `categories`
  //     remains the resolution signal — the retry logic is unchanged.
  //
  // Therefore: RESOLUTION RETRY IS UNBOUNDED. Every `categories` write
  // re-attempts resolution for as long as an `itemId` is in the URL and we have
  // not yet opened for it. Giving up is decoupled from notifying: the grace
  // window only decides when we *tell* the diner, and that notice is
  // at-most-once per id. If the item resolves later — even long after the
  // notice — we open the sheet anyway. And we never clean the URL on a miss,
  // because `itemId` is the input the retry depends on.
  //
  // AUTO-OPEN, by contrast, is at-most-once per id per mount, and is disabled
  // outright once the diner opens anything themselves. Unbounded *retry* is
  // what makes a slow menu still work; unbounded *opening* is what made the
  // sheet flap and, worse, hijack a diner's own selection.
  useEffect(() => {
    if (!itemId) {
      clearMissGrace()
      return
    }
    // The diner is driving. Their sheet is theirs; do not interrupt.
    if (dinerOpenedSheetRef.current) return
    // Already auto-opened for this id once — that promise is kept.
    if (autoOpenedItemIdsRef.current.has(itemId)) return
    // Belt and braces: never open over a sheet we did not open ourselves, even
    // if some future path opens one without going through handleProductClick.
    if (isProductSheetOpen && !openedFromDeepLinkRef.current) return
    // Empty store == "not fetched yet", never "stale id".
    if (categories.length === 0) return

    const menuItem = getMenuItemById(itemId)
    if (menuItem) {
      clearMissGrace()
      autoOpenedItemIdsRef.current.add(itemId)
      openedFromDeepLinkRef.current = true
      openProductSheetForItem(menuItem)
      return
    }

    // Miss on this pass. Nothing to give up on — the next `categories` write
    // re-runs this effect. All that's left is deciding whether to notify.
    if (noticedDeepLinkItemIdsRef.current.has(itemId)) return
    if (missGraceItemIdRef.current === itemId) return // clock already running
    clearMissGrace()
    missGraceItemIdRef.current = itemId
    missGraceTimerRef.current = setTimeout(() => {
      missGraceTimerRef.current = null
      missGraceItemIdRef.current = null
      // Re-read live store state: a rewrite could land between the last render
      // and this tick, in which case the effect opens the sheet — say nothing.
      if (getMenuItemById(itemId)) return
      noticeUnavailableOnce(itemId)
    }, DEEP_LINK_MISS_GRACE_MS)
  }, [
    itemId,
    categories,
    isProductSheetOpen,
    getMenuItemById,
    openProductSheetForItem,
    clearMissGrace,
    noticeUnavailableOnce,
  ])

  // Drop any armed grace timer on unmount (and reset the "armed for" marker so
  // a StrictMode re-mount re-arms instead of silently skipping).
  useEffect(() => clearMissGrace, [clearMissGrace])

  // Tears the sheet down without touching the URL. Split out so the
  // item-vanished path below can close the sheet while KEEPING the deep-linked
  // id in the address bar.
  const resetProductSheetState = useCallback(() => {
    setIsProductSheetOpen(false)
    setSelectedMenuItem(null)
    setQuantity(1)
    setSelectedCustomizations({})
    setIsFavorited(false)
    setIsFavoriteLoading(false)
  }, [])

  // Diner-initiated close. This is the ONLY place the deep-linked id leaves the
  // URL: once a human has seen and dismissed the sheet, the id has done its job
  // and leaving it would make Back instantly reopen the sheet.
  const handleProductSheetClose = useCallback(() => {
    resetProductSheetState()

    if (openedFromDeepLinkRef.current) {
      openedFromDeepLinkRef.current = false
      // NOTE: the auto-open latch is deliberately NOT cleared here. We already
      // showed this diner the item; re-opening it later would be an ambush.
      navigate('/menu', { replace: true })
    }
  }, [navigate, resetProductSheetState])

  // Mirror of the above: the sheet auto-opened from an early menu pass, then a
  // later pass replaced the menu WITHOUT that item. The sheet keeps its own copy
  // of the MenuItem, so it stays open and fully addable — "Add to order" would
  // put another store's item into the cart. Re-validate by id and close.
  //
  // Scoped to the deep-link case on purpose: a sheet the diner opened by
  // tapping the grid must not be yanked away by a background refetch.
  //
  // Closing here deliberately does NOT clean the URL: `itemId` is the input the
  // resolution retry depends on, and the vanish is at least as likely to mean
  // "the wrong menu just landed" as "the item died".
  //
  // It also does NOT clear the auto-open latch. We opened once, which is the
  // whole promise of the link; letting a later write reopen produced an
  // unbounded close/reopen flap (a reviewer measured 4 of each from 4
  // alternating writes) on top of a menu slot that is written twice by design.
  useEffect(() => {
    if (!isProductSheetOpen) return
    if (!openedFromDeepLinkRef.current) return
    if (!selectedMenuItem) return
    if (categories.length === 0) return
    if (getMenuItemById(selectedMenuItem.id)) return

    const goneId = selectedMenuItem.id
    resetProductSheetState()
    openedFromDeepLinkRef.current = false
    noticeUnavailableOnce(goneId)
  }, [
    categories,
    isProductSheetOpen,
    selectedMenuItem,
    getMenuItemById,
    resetProductSheetState,
    noticeUnavailableOnce,
  ])

  // Handle customization option changes
  const handleOptionChange = (customizationId: string, optionId: string, selected: boolean, isRadio: boolean) => {
    setSelectedCustomizations(prev => {
      const newSelections = { ...prev }

      if (isRadio) {
        // For radio buttons, replace all selections with just this one
        newSelections[customizationId] = new Set([optionId])
      } else {
        // For checkboxes, toggle the selection
        const currentSet = new Set(prev[customizationId] || [])
        if (selected) {
          currentSet.add(optionId)
        } else {
          currentSet.delete(optionId)
        }
        newSelections[customizationId] = currentSet
      }

      return newSelections
    })
  }

  // Calculate total price based on customizations and quantity
  const totalPrice = useMemo(() => {
    if (!selectedMenuItem) return 0

    const basePrice = getLowestVariantPrice(selectedMenuItem)
    let customizationCost = 0

    // Build a combined lookup: synthetic group + real customizations
    const allGroups = [
      ...(syntheticSizeGroup ? [syntheticSizeGroup] : []),
      ...(selectedMenuItem.customizations ?? []),
    ]

    // Sum costs from all selected options (including synthetic Size differential)
    for (const [groupId, optionIds] of Object.entries(selectedCustomizations)) {
      const group = allGroups.find(g => g.id === groupId)
      if (!group) continue
      for (const optionId of optionIds) {
        const option = group.options.find(opt => opt.id === optionId)
        if (option) customizationCost += option.price
      }
    }

    return (basePrice + customizationCost) * quantity
  }, [selectedMenuItem, syntheticSizeGroup, selectedCustomizations, quantity])

  // Calculate missing required customizations
  const missingRequirements = useMemo(() => {
    if (!selectedMenuItem) return undefined

    const allGroups = [
      ...(syntheticSizeGroup ? [syntheticSizeGroup] : []),
      ...(selectedMenuItem.customizations ?? []),
    ]

    const missingCount = allGroups.filter(group => {
      if (!group.isRequired) return false
      const selections = selectedCustomizations[group.id] || new Set()
      return selections.size === 0
    }).length

    if (missingCount === 0) return undefined
    if (missingCount === 1) return 'Select 1 required option'
    return `Select ${missingCount} required options`
  }, [selectedMenuItem, syntheticSizeGroup, selectedCustomizations])

  // Transform MenuItem customizations into option groups for the sheet
  const optionGroups = useMemo(() => {
    if (!selectedMenuItem) return []

    const allCustomizations = [
      ...(syntheticSizeGroup ? [syntheticSizeGroup] : []),
      ...(selectedMenuItem.customizations ?? []),
    ]

    return allCustomizations.map((customization) => {
      const isRadio = customization.maxSelections === 1
      const customizationSelections = selectedCustomizations[customization.id] || new Set()

      return {
        id: customization.id,
        title: customization.name,
        required: customization.isRequired,
        maxSelections: customization.maxSelections,
        children: (
          <>
            {customization.options.map((option) => (
              <OptionRow
                key={option.id}
                text={`${option.name}${option.price > 0 ? ` (+$${option.price.toFixed(2)})` : ''}`}
                optionType={isRadio ? 'radio' : 'checkbox'}
                value={option.id}
                name={customization.id}
                selected={customizationSelections.has(option.id)}
                onChange={(value, selected) => {
                  handleOptionChange(customization.id, value, selected, isRadio)
                }}
              />
            ))}
          </>
        ),
      }
    })
  }, [selectedMenuItem, syntheticSizeGroup, selectedCustomizations])

  return (
    <>
      <PageContentMenu
        categories={formattedCategories}
        onProductClick={handleProductClick}
        loading={isLoading}
        quickButtons={{
          showFavorites: !!user && favoritesCount > 0,
          showReorder: !!user && (orderHistory?.length ?? 0) > 0,
          onPressFavorites: () => navigate('/favorites'),
          onPressReorder: () => navigate('/reorder'),
        }}
        emptyStateContent={
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">No menu items available</p>
            <p className="text-sm text-gray-500">Please check back later or contact the restaurant</p>
          </div>
        }
        testID="menu-page"
        accessibilityLabel="Restaurant menu with food categories and items"
      />

      {/* Product Detail Sheet */}
      <Sheet
        isOpen={isProductSheetOpen}
        onClose={handleProductSheetClose}
        title={selectedMenuItem?.name || 'Product Details'}
        testID="product-detail-sheet"
      >
        {selectedMenuItem && (
          <SheetContentProduct
            showSectionRequiredProduct={false}
            sectionProductInformation={{
              productName: selectedMenuItem.name,
              description: selectedMenuItem.description || '',
              currentPrice: (() => {
                const p = getLowestVariantPrice(selectedMenuItem)
                // Priceless item ("market price" scrape): no price line, and
                // NEVER undefined.toFixed (render crash) or $NaN.
                return typeof p === 'number' && Number.isFinite(p) ? `$${p.toFixed(2)}` : ''
              })(),
              imageUrl: selectedMenuItem.imageUrl,
            }}
            optionGroups={optionGroups}
            sectionAddToOrder={{
              price: Number.isFinite(totalPrice) ? `$${totalPrice.toFixed(2)}` : '',
              quantity: quantity,
              onQuantityChange: setQuantity,
              isFavorited: isFavorited,
              onFavoriteToggle: handleFavoriteToggle,
              disabled: isFavoriteLoading,
              missingRequirements: missingRequirements,
              onAddToOrder: () => {
                if (isShowcase()) {
                  showShowcaseNotice()
                  handleProductSheetClose()
                  return
                }

                // Extract selected variant name from synthetic Size group
                let selectedPriceVariant: string | undefined
                const sizeSelections = selectedCustomizations[SYNTHETIC_SIZE_GROUP_ID]
                if (sizeSelections?.size) {
                  const selectedOptionId = Array.from(sizeSelections)[0]
                  if (selectedOptionId) {
                    selectedPriceVariant = variantKeyFromOptionId(selectedOptionId)
                  }
                }

                // Convert selectedCustomizations, filtering out synthetic Size group
                const customizationSelections = Array.from(
                  Object.entries(selectedCustomizations)
                )
                  .filter(([customizationItemId]) => !isSyntheticSizeOption(customizationItemId))
                  .flatMap(([customizationItemId, optionIds]) =>
                    Array.from(optionIds).map(selectedOptionId => ({
                      customizationItemId,
                      selectedOptionId,
                    }))
                  )

                // Add to cart
                addItem(selectedMenuItem, quantity, customizationSelections,
                  selectedPriceVariant ? { selectedPriceVariant } : undefined
                )

                safeGetLogger()?.cart('item_added', { menuItem: selectedMenuItem?.name, quantity, totalPrice })

                handleProductSheetClose()
              },
            }}
            testID="product-detail-content"
          />
        )}
      </Sheet>
    </>
  )
}